import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const WhatsAppButton = () => {
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const [wiggle, setWiggle] = useState(false);

  useEffect(() => {
    // Trigger wiggle animation every 12 seconds
    const interval = setInterval(() => {
      setWiggle(true);
      const timer = setTimeout(() => setWiggle(false), 1000);
      return () => clearTimeout(timer);
    }, 12000);

    return () => clearInterval(interval);
  }, []);

  const getWhatsAppUrl = () => {
    // Read phone from env or fallback to Kidilan's contact
    const rawPhone = import.meta.env.VITE_WHATSAPP_PHONE || "919847065200";
    // Clean phone to only digits
    const phone = rawPhone.replace(/\D/g, "");

    let message = "";
    if (location.pathname.startsWith("/product/")) {
      const productName = document.querySelector("h1")?.innerText || document.title;
      // Dynamically extract price from price element if visible
      const priceElement = document.querySelector(".text-slate-900.font-bold");
      const price = priceElement ? priceElement.innerText.trim() : "";
      const priceDetails = price ? ` (${price})` : "";

      message = `Hi! I'm visiting kidilan.com and I'm interested in the product: *${productName}*${priceDetails}.\n\nLink: ${window.location.href}\n\nCan you please provide more details?`;
    } else {
      message = "Hi! I'm visiting kidilan.com and have a question regarding your products.";
    }

    return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: "24px",
        right: "24px",
        zIndex: 999999,
        display: "flex",
        alignItems: "center",
      }}
    >
      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial={{ opacity: 0, x: 20, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 15, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="mr-3 pointer-events-none select-none rounded-xl bg-white/95 dark:bg-slate-900/95 backdrop-blur-md px-4 py-2 text-sm font-semibold text-slate-800 dark:text-slate-100 shadow-xl border border-slate-200/50 dark:border-slate-800/50 whitespace-nowrap"
          >
            {location.pathname.startsWith("/product/")
              ? "Ask about this product! 🛒"
              : "Chat with us! 💬"}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.a
        href={getWhatsAppUrl()}
        target="_blank"
        rel="noopener noreferrer"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        initial={{ scale: 0, opacity: 0, y: 30 }}
        animate={
          wiggle
            ? {
                scale: [1, 1.1, 1.1, 1.1, 1.1, 1],
                rotate: [0, -10, 10, -10, 10, 0],
                opacity: 1,
                y: 0,
              }
            : { scale: 1, opacity: 1, y: 0 }
        }
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        transition={
          wiggle
            ? { duration: 0.8 }
            : { type: "spring", stiffness: 260, damping: 20 }
        }
        className="relative flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-tr from-emerald-500 to-green-400 text-white shadow-[0_8px_30px_rgba(16,185,129,0.4)] transition-shadow duration-300 hover:shadow-[0_8px_35px_rgba(16,185,129,0.6)] cursor-pointer"
        aria-label="Chat on WhatsApp"
      >
        {/* Soft breathing pulse ripple background */}
        <motion.div
          animate={{
            scale: [1, 1.35, 1],
            opacity: [0.5, 0, 0.5],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute inset-0 -z-10 rounded-full bg-emerald-500 blur-[3px]"
        />

        {/* WhatsApp Brand Icon SVG */}
        <svg
          className="h-8 w-8 fill-current relative z-10 drop-shadow-[0_2px_4px_rgba(0,0,0,0.15)]"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.248 8.477 3.517 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.504-5.714-1.465L0 24zm6.59-4.846c1.62.969 3.238 1.455 4.881 1.456 5.48 0 9.942-4.614 9.945-10.287.002-2.748-1.01-5.333-2.854-7.178C16.772 1.3 14.224.28 11.96.28c-5.483 0-9.943 4.615-9.947 10.289-.001 2.016.521 3.987 1.516 5.711l-1.011 3.693 3.824-1.002.325.193zm11.332-6.289c-.313-.156-1.848-.91-2.133-1.014-.286-.105-.493-.156-.7.156-.207.31-.8.91-.98 1.117-.18.207-.36.233-.672.078-.313-.156-1.32-.486-2.515-1.55-1.037-.925-1.464-1.63-1.62-1.942-.156-.312-.016-.48.14-.636.14-.14.313-.36.47-.542.156-.18.208-.31.312-.518.104-.207.052-.39-.026-.546-.078-.156-.7-1.688-.958-2.311-.25-.603-.504-.52-.689-.53l-.587-.01c-.207 0-.546.078-.832.39-.286.31-1.092 1.065-1.092 2.599 0 1.534 1.117 3.014 1.273 3.22.156.208 2.197 3.356 5.323 4.706.743.32 1.324.512 1.776.656.748.238 1.43.204 1.97.123.6-.09 1.847-.754 2.107-1.444.26-.69.26-1.286.18-1.416-.078-.13-.286-.208-.6-.364z" />
        </svg>
      </motion.a>
    </div>
  );
};

export default WhatsAppButton;
